import { useMemo, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useTourGuide, TourGuideConfig } from "./useTourGuide";
import { useAutoTourGuide } from "./useAutoTourGuide";
import { useTourGuideHover } from "./useTourGuideHover";
import { useTourGuideContext } from "@root/context/tour-guide-context";
import { updateTourCompletion } from "@store/thunks/userProfile";
import { ROUTE_NAMES } from "@root/constants";
import { TourGuideType } from "@root/constants/tour-guide";

/**
 * Tour Guide Hook for Journalist Dashboard
 *
 * Guides users through:
 * - Sidebar tabs (Dashboard, Courses, My Learning, Customer Center, Notice)
 * - Language toggler
 * - Nav control (profile – view/update on click)
 * - KPEC official website link
 * - Recent Learning, Certificates, Recommended for you
 */
export const useJournalistDashboardTourGuide = () => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const dispatch = useDispatch();
  const { setActiveTourModule, setCurrentTourStep } = useTourGuideContext();

  const isOnJournalistDashboard = pathname === ROUTE_NAMES.DASHBOARD;

  const config: TourGuideConfig = useMemo(
    () => ({
      moduleId: "journalistDashboard",
      enabled: isOnJournalistDashboard,
      steps: [
        {
          id: "sidebar-dashboard",
          targetSelector: '[data-tour-item="sidebar-menu-0"]',
          title: t("tourGuide.journalistDashboard.sidebarStep1.title"),
          description: t(
            "tourGuide.journalistDashboard.sidebarStep1.description",
          ),
          position: "right",
          offset: { x: 15, y: 0 },
          highlightPadding: 10,
        },
        {
          id: "sidebar-courses",
          targetSelector: '[data-tour-item="sidebar-menu-1"]',
          title: t("tourGuide.journalistDashboard.sidebarStep2.title"),
          description: t(
            "tourGuide.journalistDashboard.sidebarStep2.description",
          ),
          position: "right",
          offset: { x: 15, y: 0 },
          highlightPadding: 10,
        },
        {
          id: "sidebar-my-learning",
          targetSelector: '[data-tour-item="sidebar-menu-2"]',
          title: t("tourGuide.journalistDashboard.sidebarStep3.title"),
          description: t(
            "tourGuide.journalistDashboard.sidebarStep3.description",
          ),
          position: "right",
          offset: { x: 15, y: 0 },
          highlightPadding: 10,
        },
        {
          id: "sidebar-customer-center",
          targetSelector: '[data-tour-item="sidebar-menu-3"]',
          title: t("tourGuide.journalistDashboard.sidebarStep4.title"),
          description: t(
            "tourGuide.journalistDashboard.sidebarStep4.description",
          ),
          position: "right",
          offset: { x: 15, y: 0 },
          highlightPadding: 10,
        },
        {
          id: "sidebar-notice",
          targetSelector: '[data-tour-item="sidebar-menu-4"]',
          title: t("tourGuide.journalistDashboard.sidebarStep5.title"),
          description: t(
            "tourGuide.journalistDashboard.sidebarStep5.description",
          ),
          position: "right",
          offset: { x: 15, y: 0 },
          highlightPadding: 10,
        },
        {
          id: "sidebar-language",
          targetSelector: '[data-tour-item="sidebar-language-toggle"]',
          title: t("tourGuide.journalistDashboard.sidebarLanguage.title"),
          description: t(
            "tourGuide.journalistDashboard.sidebarLanguage.description",
          ),
          position: "right",
          offset: { x: 15, y: 0 },
          highlightPadding: 10,
        },
        {
          id: "nav-control",
          targetSelector: '[data-tour-item="journalist-nav-control"]',
          title: t("tourGuide.journalistDashboard.navControl.title"),
          description: t(
            "tourGuide.journalistDashboard.navControl.description",
          ),
          position: "right",
          offset: { x: 15, y: 0 },
          highlightPadding: 10,
        },
        {
          id: "kpec-home",
          targetSelector: '[data-tour-item="header-home-button"]',
          title: t("tourGuide.journalistDashboard.step1.title"),
          description: t("tourGuide.journalistDashboard.step1.description"),
          position: "bottom",
          offset: { x: 0, y: 15 },
          highlightPadding: 10,
        },
        {
          id: "recent-learning",
          targetSelector: '[data-tour-item="journalist-recent-learning"]',
          title: t("tourGuide.journalistDashboard.step2.title"),
          description: t("tourGuide.journalistDashboard.step2.description"),
          position: "right",
          offset: { x: 15, y: 0 },
          highlightPadding: 12,
        },
        {
          id: "certificates",
          targetSelector: '[data-tour-item="journalist-certificates"]',
          title: t("tourGuide.journalistDashboard.step3.title"),
          description: t("tourGuide.journalistDashboard.step3.description"),
          position: "right",
          offset: { x: 15, y: 0 },
          highlightPadding: 12,
        },
        {
          id: "course-cards",
          targetSelector: '[data-tour-item="journalist-course-cards"]',
          title: t("tourGuide.journalistDashboard.step4.title"),
          description: t("tourGuide.journalistDashboard.step4.description"),
          position: "right",
          offset: { x: 15, y: 0 },
          highlightPadding: 12,
        },
      ],
    }),
    [isOnJournalistDashboard, t],
  );

  const baseTourGuide = useTourGuide(config);

  const handleTourComplete = () => {
    dispatch(
      updateTourCompletion({
        payload: {
          tourGuides: [
            { type: TourGuideType.JournalistDashboard, isDone: true },
          ],
        },
        onSuccess: () => {},
        onError: () => {},
      }) as any,
    );
  };

  const journalistDashboardTourGuide = useAutoTourGuide({
    tourGuide: baseTourGuide,
    tourGuideType: TourGuideType.JournalistDashboard,
    isOnTourPath: isOnJournalistDashboard,
    onTourComplete: handleTourComplete,
  });

  useEffect(() => {
    if (journalistDashboardTourGuide.isActive) {
      setActiveTourModule("journalistDashboard");
      setCurrentTourStep(journalistDashboardTourGuide.currentStep);
    } else {
      setActiveTourModule(null);
    }
  }, [
    journalistDashboardTourGuide.isActive,
    journalistDashboardTourGuide.currentStep,
    setActiveTourModule,
    setCurrentTourStep,
  ]);

  const kpecHomeHover = useTourGuideHover("kpec-home", config.steps[7]);
  const recentLearningHover = useTourGuideHover(
    "recent-learning",
    config.steps[8],
  );
  const certificatesHover = useTourGuideHover("certificates", config.steps[9]);
  const courseCardsHover = useTourGuideHover("course-cards", config.steps[10]);

  return {
    journalistDashboardTourGuide,
    kpecHomeHover,
    recentLearningHover,
    certificatesHover,
    courseCardsHover,
  };
};
